// PerformanceView.jsx (FULL) — polished layout + SETTINGS with presets
import React, { useMemo } from "react";
import Card from "./components/Card";
import RingGauge from "./components/RingGauge";
import Sparkline from "./components/Sparkline";
import HealthPanel from "./components/HealthPanel";
import { fmtBytes, fmtMs, fmtNum, fmtPct01 } from "./utils/format";

const WINDOW_PRESETS = [
  { label: "Live", minutes: 2, hint: "Fast in Echtzeit" },
  { label: "Kurz", minutes: 5, hint: "Die letzten Minuten" },
  { label: "Mittel", minutes: 15, hint: "Geglättet" },
  { label: "Lang", minutes: 30, hint: "Trend" },
];

export default function PerformanceView({
  metrics,
  metricsErr,
  windowMinutes,
  setWindowMinutes,
  history = [],
}) {
  const memUsed = metrics?.jvm?.usedBytes ?? 0;
  const memMax = metrics?.jvm?.maxBytes ?? 0;
  const memRatio = memMax > 0 ? memUsed / memMax : 0;

  const cpu = metrics?.cpu?.process ?? 0;

  const db = metrics?.dbPool || {};
  const dbActive = db.active ?? 0;
  const dbMax = db.max ?? 0;
  const dbRatio = dbMax > 0 ? dbActive / dbMax : 0;

  const req = metrics?.requests || {};
  const lat = metrics?.latency || {};

  // Timestamped series for charts
  const p95Series = useMemo(
    () =>
      history.map((h, i) => ({
        t: Number(h?.t ?? Date.now() - (history.length - 1 - i) * 1000),
        v: Number(h?.p95Ms ?? 0),
      })),
    [history]
  );

  const rpsSeries = useMemo(
    () =>
      history.map((h, i) => ({
        t: Number(h?.t ?? Date.now() - (history.length - 1 - i) * 1000),
        v: Number(h?.rps ?? 0),
      })),
    [history]
  );

  const rps5xxSeries = useMemo(
    () =>
      history.map((h, i) => ({
        t: Number(h?.t ?? Date.now() - (history.length - 1 - i) * 1000),
        v: Number(h?.rps5xx ?? 0),
      })),
    [history]
  );

  const maxRps = useMemo(
    () => Math.max(...rpsSeries.map((p) => p.v), 1),
    [rpsSeries]
  );

  return (
    <div>
      {metricsErr && (
        <div style={{ marginBottom: 12, color: "#b91c1c", whiteSpace: "pre-wrap" }}>
          {metricsErr}
        </div>
      )}

      <HealthPanel metrics={metrics} metricsErr={metricsErr} historyCount={history.length} />

      {/* GAUGES */}
      <div style={grid3}>
        <Card title="CPU">
          <RingGauge
            value01={cpu}
            label="Auslastung des Prozesses"
            helper="Warnung ab 80 % · kritisch ab 95 %"
            centerTop={fmtPct01(cpu)}
            centerBottom="Prozess"
            warnAt={0.8}
            critAt={0.95}
          />
        </Card>

        <Card title="JVM-Speicher">
          <RingGauge
            value01={memRatio}
            label="Heap-Auslastung"
            helper="Warnung ab 85 % · kritisch ab 95 %"
            centerTop={`${Math.round(memRatio * 100)}%`}
            centerBottom={fmtBytes(memUsed)}
            warnAt={0.85}
            critAt={0.95}
          />
          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={chipStyle()}>{fmtBytes(memUsed)} / {fmtBytes(memMax)}</span>
          </div>
        </Card>

        <Card title="DB Pool">
          <RingGauge
            value01={dbRatio}
            label="Pool-Auslastung (Hikari)"
            helper="Warnung ab 85 % · kritisch ab 95 %"
            centerTop={`${Math.round(dbRatio * 100)}%`}
            centerBottom={`aktiv ${fmtNum(dbActive)}`}
            warnAt={0.85}
            critAt={0.95}
          />
          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={chipStyle()}>Aktiv {fmtNum(db.active)}</span>
            <span style={chipStyle()}>Frei {fmtNum(db.idle)}</span>
            <span style={chipStyle()}>Wartend {fmtNum(db.pending)}</span>
          </div>
        </Card>
      </div>

      {/* LATENCY + TRAFFIC */}
      <div style={grid2}>
        <Card
          title="Antwortzeit (HTTP)"
          right={<div style={{ fontSize: 12, opacity: 0.75 }}>p95 · p99 · max</div>}
        >
          <div style={statsRow}>
            <StatChip label="p95" value={fmtMs(lat.p95Ms)} />
            <StatChip label="p99" value={fmtMs(lat.p99Ms)} />
            <StatChip label="max" value={fmtMs(lat.maxMs)} />
          </div>

          <div style={{ marginTop: 10 }}>
            <Sparkline
              data={p95Series}
              height={210}
              zones={[
                { from: 800, to: 2000, fill: "#b45309", opacity: 0.10 },
                { from: 2000, to: 1e9, fill: "#dc2626", opacity: 0.08 },
              ]}
              format={(v) => `${Math.round(v)}ms`}
              xFormat={(t) =>
                new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              }
              yLabel="ms"
            />
          </div>

          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
            Thresholds: warn ≥ 800ms · critical ≥ 2000ms
          </div>
        </Card>

        <Card title="Anfragen & Fehler">
          <div style={statsGrid}>
            <StatChip label="RPS" value={fmtNum(req.approxRps)} sub="ca." />
            <StatChip label="RPM" value={fmtNum(req.approxRpm)} sub="ca." />
            <StatChip
              label="5xx"
              value={fmtNum(req.server5xxCount)}
              sub={`aktuell ${fmtNum(req.approx5xxRps)} rps`}
            />
            <StatChip
              label="4xx"
              value={fmtNum(req.client4xxCount)}
              sub={`aktuell ${fmtNum(req.approx4xxRps)} rps`}
            />
          </div>

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            <div style={miniChartWrap}>
              <div style={miniChartTitle}>Anfragen (RPS)</div>
              <Sparkline
                data={rpsSeries}
                height={140}
                zones={[{ from: 0, to: maxRps * 0.85, fill: "#64748b", opacity: 0.06 }]}
                format={(v) => `${fmtNum(v)}`}
                xFormat={(t) =>
                  new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                }
                yLabel="rps"
              />
            </div>

            <div style={miniChartWrap}>
              <div style={miniChartTitle}>Serverfehler (5xx RPS)</div>
              <Sparkline
                data={rps5xxSeries}
                height={140}
                zones={[
                  { from: 0.2, to: 1.0, fill: "#b45309", opacity: 0.10 },
                  { from: 1.0, to: 1e9, fill: "#dc2626", opacity: 0.08 },
                ]}
                format={(v) => `${fmtNum(v)}`}
                xFormat={(t) =>
                  new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                }
                yLabel="5xx rps"
              />
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
            Total (since start): {fmtNum(req.totalCount)}
          </div>
        </Card>
      </div>

      {/* SETTINGS (with presets) */}
      <div style={{ marginTop: 12 }}>
        <Card title="Zeitfenster">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                Vorgaben
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {WINDOW_PRESETS.map((p) => {
                  const active = windowMinutes === p.minutes;
                  return (
                    <button
                      key={p.minutes}
                      onClick={() => setWindowMinutes(p.minutes)}
                      title={p.hint}
                      style={{
                        height: 32,
                        padding: "0 12px",
                        borderRadius: 999,
                        border: active
                          ? "1px solid rgba(0,108,253,0.4)"
                          : "1px solid rgba(15,23,42,0.12)",
                        background: active
                          ? "rgba(0,108,253,0.1)"
                          : "rgba(15,23,42,0.055)",
                        color: "#15171c",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.label}
                      <span style={{ opacity: 0.75 }}>{p.minutes}m</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <label
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span>Eigenes Zeitfenster</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={windowMinutes}
                  onChange={(e) => setWindowMinutes(Number(e.target.value))}
                  style={inputStyle}
                />
                <span style={{ opacity: 0.75 }}>Minuten</span>
              </label>

              <div style={{ fontSize: 12, opacity: 0.7 }}>
                Alle Werte oben gelten für dieses Zeitfenster.
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ---------- Small UI primitives (keeps everything consistent) ---------- */

function StatChip({ label, value, sub }) {
  return (
    <div style={statChip}>
      <div style={statLabel}>{label}</div>
      <div style={statValue}>{value}</div>
      {sub ? <div style={statSub}>{sub}</div> : null}
    </div>
  );
}

const grid3 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: 12,
  marginTop: 12,
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 12,
  marginTop: 12,
};

const statsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const statChip = {
  borderRadius: 14,
  padding: "10px 12px",
  background: "#f8f9fb",
  border: "1px solid rgba(15,23,42,0.088)",
  boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
  minWidth: 120,
};

const statLabel = {
  fontSize: 11,
  opacity: 0.75,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  fontWeight: 800,
};

const statValue = {
  marginTop: 4,
  fontSize: 18,
  fontWeight: 900,
  letterSpacing: 0.2,
};

const statSub = {
  marginTop: 4,
  fontSize: 11,
  opacity: 0.75,
};

const miniChartWrap = {
  borderRadius: 14,
  padding: 10,
  background: "#f8f9fb",
  border: "1px solid rgba(15,23,42,0.088)",
};

const miniChartTitle = {
  fontSize: 11,
  opacity: 0.75,
  fontWeight: 900,
  letterSpacing: 0.2,
  marginBottom: 6,
};

const inputStyle = {
  width: 90,
  height: 34,
  borderRadius: 10,
  border: "1px solid rgba(15,23,42,0.12)",
  background: "#f8f9fb",
  color: "#15171c",
  padding: "0 10px",
  outline: "none",
};

function chipStyle() {
  return {
    display: "inline-flex",
    alignItems: "center",
    height: 22,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: 0.15,
    border: "1px solid rgba(15,23,42,0.11)",
    background: "rgba(15,23,42,0.044)",
    color: "#15171c",
    whiteSpace: "nowrap",
  };
}
